// =============================================================================
// AGC: [AUDIT] LotAuditLogCard - Collapsed card showing audit log summary
// AGC: Provides quick access to view and download lot audit logs
// =============================================================================

import React, { useState, useEffect } from 'react'
import { FileText, Download, Eye, Clock, ChevronDown, ChevronUp, Loader2, AlertCircle } from 'lucide-react'
import { Button } from '../shared'
import { getLotAuditLog, downloadAuditLog } from '../../services/audit'
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../hooks/useTranslation'
import type { LotAuditLog, AuditExportFormat } from '../../types'

interface LotAuditLogCardProps {
  lotId: string
  lotName: string
  fieldId: string
  onViewLog: () => void // AGC: Callback to open full audit log viewer
}

/**
 * AGC: [AUDIT] Format date for display
 */
const formatDate = (isoString: string | undefined): string => {
  if (!isoString) return 'N/A'
  try {
    return new Date(isoString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return 'N/A'
  }
}

export const LotAuditLogCard: React.FC<LotAuditLogCardProps> = ({
  lotId,
  lotName,
  fieldId,
  onViewLog,
}) => {
  const { t } = useTranslation()
  const { getAuthToken, user } = useAuth()
  
  // AGC: State
  const [isExpanded, setIsExpanded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [auditLog, setAuditLog] = useState<LotAuditLog | null>(null)
  const [error, setError] = useState<string | null>(null)
  
  // AGC: Fetch audit log summary when expanded
  useEffect(() => {
    const fetchAuditLog = async () => {
      if (!isExpanded || !lotId || !fieldId) return
      
      setIsLoading(true)
      setError(null)
      
      try {
        const token = await getAuthToken()
        const log = await getLotAuditLog(lotId, fieldId, token, user?.uid, {
          tracesLimit: 1,
          eventsLimit: 5,
        })
        setAuditLog(log)
      } catch (err) {
        console.error('❌ AGC: [AUDIT] Error fetching audit log:', err)
        setError(t('audit.errorLoading'))
      } finally {
        setIsLoading(false)
      }
    }
    
    fetchAuditLog()
  }, [isExpanded, lotId, fieldId, getAuthToken, user?.uid, t])
  
  // AGC: Handle download
  const handleDownload = async (format: AuditExportFormat) => {
    setIsDownloading(true)
    try {
      const token = await getAuthToken()
      const success = await downloadAuditLog(lotId, fieldId, format, lotName, token, user?.uid)
      if (!success) {
        console.error('❌ AGC: [AUDIT] Download failed')
      }
    } catch (err) {
      console.error('❌ AGC: [AUDIT] Download error:', err)
    } finally {
      setIsDownloading(false)
    }
  }
  
  // AGC: Get event type label
  const getEventTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      LOT_CREATED: t('audit.events.lotCreated'),
      LOT_UPDATED: t('audit.events.lotUpdated'),
      LOT_ARCHIVED: t('audit.events.lotArchived'),
      LOT_UNARCHIVED: t('audit.events.lotUnarchived'),
      TASK_CREATED: t('audit.events.taskCreated'),
      TASK_UPDATED: t('audit.events.taskUpdated'),
      TASK_DELETED: t('audit.events.taskDeleted'),
      TASK_COMPLETED: t('audit.events.taskCompleted'),
      INPUTS_GENERATED: t('audit.events.inputsGenerated'),
      RECOMMENDATION_GENERATED: t('audit.events.recommendationGenerated'),
      PLAN_GENERATED: t('audit.events.planGenerated'),
    }
    return labels[type] || type
  }
  
  return (
    <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
      {/* AGC: Card Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
            <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900 dark:text-white">{t('audit.title')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('audit.subtitle')}</p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      
      {/* AGC: Expanded Content */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-600">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
              <span className="ml-2 text-gray-500">{t('common.loading')}</span>
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 text-amber-600 py-4">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          ) : auditLog ? (
            <>
              {/* AGC: Summary Stats */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-blue-600">{auditLog.totalTraces}</div>
                  <div className="text-xs text-gray-500">{t('audit.tracesCount')}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-emerald-600">{auditLog.totalEvents}</div>
                  <div className="text-xs text-gray-500">{t('audit.eventsCount')}</div>
                </div>
              </div>
              
              {/* AGC: Latest Trace Info */}
              {auditLog.latestTrace && (
                <div className="bg-blue-50 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">{t('audit.latestTrace')}</span>
                  </div>
                  <p className="text-sm text-blue-700">
                    {formatDate(auditLog.latestTrace.timestamp)}
                  </p>
                  {auditLog.latestTrace.results?.recommendedCrop && (
                    <p className="text-xs text-blue-600 mt-1">
                      {t('audit.recommendedCrop')}: {auditLog.latestTrace.results.recommendedCrop}
                    </p>
                  )}
                </div>
              )}
              
              {/* AGC: Recent Events Preview */}
              {auditLog.eventLogs && auditLog.eventLogs.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">{t('audit.recentEvents')}</h4>
                  <div className="space-y-2">
                    {auditLog.eventLogs.slice(0, 3).map((event) => (
                      <div 
                        key={event.id} 
                        className="flex items-center justify-between text-sm py-1 border-b border-gray-100 last:border-0"
                      >
                        <span className="text-gray-700">{getEventTypeLabel(event.type)}</span>
                        <span className="text-gray-400 text-xs">{formatDate(event.timestamp)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* AGC: Action Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    console.log('🔍 AGC: [AUDIT_CARD] View Full Log clicked')
                    onViewLog()
                  }}
                  variant="secondary"
                  size="sm"
                  className="flex-1"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {t('audit.viewFull')}
                </Button>
                
                <div className="relative group">
                  <Button
                    onClick={() => handleDownload('json')}
                    variant="secondary"
                    size="sm"
                    disabled={isDownloading}
                  >
                    {isDownloading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        JSON
                      </>
                    )}
                  </Button>
                </div>
                
                <Button
                  onClick={() => handleDownload('xml')}
                  variant="secondary"
                  size="sm"
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      XML
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-6 text-gray-500 dark:text-gray-400">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>{t('audit.noData')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default LotAuditLogCard


